import { LogEntriesImageUploadModal } from '@monorepo/logs/src/components/LogEntriesReviewModal/LogEntriesImageUploadModal';
import { Button } from '@monorepo/shared/componentsV2/Button';
import { ErrorBoundary } from '@monorepo/shared/componentsV2/ErrorBoundary';
import { ComboboxField } from '@monorepo/shared/componentsV2/fields/ComboboxField';
import { SwitchField } from '@monorepo/shared/componentsV2/fields/SwitchField';
import {
  Content,
  Page,
  Title,
} from '@monorepo/shared/componentsV2/fullPageWizard/styled/fullPageWizard';
import {
  InfoCard,
  InfoCardVariant,
} from '@monorepo/shared/componentsV2/InfoCard';
import { Select } from '@monorepo/shared/componentsV2/Select/Select';
import {
  StyledFormGroup,
  StyledSelect,
  StyledTextArea,
} from '@monorepo/shared/componentsV2/styled/form';
import { useLog } from '@monorepo/shared/hooks/logs/useLog';
import { useDisableSubnav } from '@monorepo/shared/hooks/subnav/useDisableSubnav';
import { useOpenClose } from '@monorepo/shared/hooks/useOpenClose';
import { useOrganizationProjects } from '@monorepo/shared/hooks/useOrgProjects';
import { SelectableOption } from '@monorepo/shared/types/SelectableOption';
import ArrowLineUpIcon from '@svg/arrow-line-up.svg';
import {
  DEFAULT_MAPLE_AI_MODEL_ID,
  getDefaultReasoningEffort,
  MAPLE_AI_MODELS,
  ReasoningEffort,
  UpdateLogRequest,
} from 'mapistry-shared';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Form, useField, useForm, useFormState } from 'react-final-form';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { EditLogHeader, WizardStep } from './EditLogHeader';
import { Mode } from './types';
import { useSubmitLogDetails } from './useSubmitLogDetails';

const StyledComboboxField = styled(ComboboxField)`
  width: 18rem;
` as typeof ComboboxField;

const CompactFormSection = styled.div`
  .MuiFormHelperText-root {
    line-height: 0.2rem;
  }
`;

const CompactFormGroup = styled(StyledFormGroup)`
  margin-bottom: 0.75rem;
`;

const StyledFormGroupContainer = styled(CompactFormGroup).attrs({ row: true })`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  align-items: center;
  width: 37rem;
`;

const StyledSelectProject = styled(Select)`
  width: 18rem;
  margin-top: -14px;
  margin-left: 1rem;
` as typeof Select;

const StyledWarningInfoCard = styled(InfoCard)`
  width: 35rem;
  margin-top: 1rem;
`;

const reasoningEffortOptions: SelectableOption<ReasoningEffort>[] = [
  { label: 'None', value: ReasoningEffort.NONE },
  { label: 'Minimal', value: ReasoningEffort.MINIMAL },
  { label: 'Low', value: ReasoningEffort.LOW },
  { label: 'Medium', value: ReasoningEffort.MEDIUM },
  { label: 'High', value: ReasoningEffort.HIGH },
  { label: 'X-High', value: ReasoningEffort.XHIGH },
];

const hasNoReasoningEffort = (modelId: string): boolean => {
  const standardModel = MAPLE_AI_MODELS[modelId];
  return (
    standardModel !== undefined &&
    standardModel.supportedReasoningEfforts.length === 0
  );
};

const getReasoningEffortOptions = (
  modelId?: string | null,
): SelectableOption<ReasoningEffort>[] => {
  if (!modelId) return reasoningEffortOptions;
  const standardModel = MAPLE_AI_MODELS[modelId];
  if (!standardModel) return reasoningEffortOptions; // custom models
  if (standardModel.supportedReasoningEfforts.length === 0) return [];
  return reasoningEffortOptions.filter((opt) =>
    standardModel.supportedReasoningEfforts.includes(opt.value),
  );
};

const EdpAiConfigFieldNames = {
  MODEL_ID: 'edpAiConfiguration.modelId',
  REASONING_EFFORT: 'edpAiConfiguration.reasoningEffort',
  AI_PROMPT: 'edpAiConfiguration.aiPrompt',
  IS_ENABLED: 'edpAiConfiguration.isEnabled',
};

interface AiConfigFieldsProps {
  isEditMode: boolean;
  logId: string;
  organizationId: string;
}

function AiConfigFields({
  isEditMode,
  logId,
  organizationId,
}: AiConfigFieldsProps) {
  const [isUploadModalOpen, openUploadModal, closeUploadModal] = useOpenClose();
  const { projects } = useOrganizationProjects({
    organizationId,
  });
  const projectMap = useMemo(
    () =>
      Object.fromEntries(
        (projects ?? []).map((project) => [project.id, project]),
      ),
    [projects],
  );
  const [projectId, setProjectId] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (!projectId && projects?.[0]?.id) {
      setProjectId(projects[0].id);
    }
  }, [projects, projectId]);
  const form = useForm();
  const { pristine, submitting } = useFormState({
    subscription: { pristine: true, submitting: true },
  });

  const { input: modelIdInput } = useField<string>(
    EdpAiConfigFieldNames.MODEL_ID,
    { subscription: { value: true } },
  );
  // final-form reports an unset field as '', but getDefaultReasoningEffort only falls back on null/undefined
  const modelId = modelIdInput.value || undefined;

  useEffect(() => {
    if (!modelId) return;

    const standardModel = MAPLE_AI_MODELS[modelId];
    if (!standardModel) return; // no model or custom model

    const currentValue = form.getFieldState(
      EdpAiConfigFieldNames.REASONING_EFFORT,
    )?.value;

    // model does not support reasoning effort, clear the field
    if (standardModel.supportedReasoningEfforts.length === 0) {
      if (currentValue != null) {
        form.change(EdpAiConfigFieldNames.REASONING_EFFORT, null);
      }
      return;
    }

    // current value is not in the supported reasoning efforts or is null, set the first supported effort
    if (
      (currentValue != null &&
        !standardModel.supportedReasoningEfforts.includes(currentValue)) ||
      currentValue == null
    ) {
      form.change(
        EdpAiConfigFieldNames.REASONING_EFFORT,
        standardModel.supportedReasoningEfforts[0],
      );
    }
  }, [form, modelId]);

  const warningRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (pristine) return undefined;

    const timeoutId = setTimeout(() => {
      warningRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [pristine]);

  const modelOptions = useMemo(
    () =>
      Object.keys(MAPLE_AI_MODELS).map((model) => ({
        label: model,
        value: model,
      })),
    [],
  );

  const handleUploadTestFile = useCallback(() => {
    openUploadModal();
  }, [openUploadModal]);

  return (
    <>
      <EditLogHeader
        canSubmit={!pristine || !isEditMode}
        isSubmitting={submitting}
        onSubmit={form.submit}
        wizardStep={WizardStep.AI}
      />
      <Content>
        <Title>AI Assisted Submissions</Title>
        <SwitchField
          name={EdpAiConfigFieldNames.IS_ENABLED}
          checkedLabel="On"
          uncheckedLabel="Off"
          label="Activate custom prompt"
          inputProps={{
            'aria-label': 'Activate custom prompt',
          }}
        />
        <CompactFormSection>
          <CompactFormGroup row>
            <StyledComboboxField
              label="Model ID"
              name={EdpAiConfigFieldNames.MODEL_ID}
              placeholder={DEFAULT_MAPLE_AI_MODEL_ID}
              options={modelOptions}
              allowCustomValue
              tooltipText={
                <>
                  <strong>
                    Different models trade off speed and reasoning depth.
                  </strong>{' '}
                  <br />
                  Larger and more recent models (those at the top of this list)
                  handle more complex document extraction but will respond more
                  slowly than models lower on this list. We recommend you start
                  with gpt-5-mini and experiment with different prompts. If your
                  prompt works well, try a model lower on this list; you might
                  get faster results with the same level of accuracy. If you are
                  unable to get accurate results after experimenting with your
                  prompt, try a model higher up on this list.
                </>
              }
            />
          </CompactFormGroup>
          <StyledFormGroupContainer row>
            <StyledSelect<
              NonNullable<
                NonNullable<
                  UpdateLogRequest['edpAiConfiguration']
                >['reasoningEffort']
              >
            >
              label="Reasoning Effort"
              name={EdpAiConfigFieldNames.REASONING_EFFORT}
              options={getReasoningEffortOptions(modelId)}
              placeholder={getDefaultReasoningEffort(modelId).label}
              tooltipText="Reasoning effort controls how much internal deliberation the model performs before producing its final answer, trading off speed against depth and reliability of reasoning. Some models (such as gpt-4.1 and gpt-4.1-mini) do not support reasoning effort."
              disabled={hasNoReasoningEffort(modelId ?? '')}
            />
          </StyledFormGroupContainer>
          <CompactFormGroup row>
            <StyledTextArea<
              NonNullable<
                NonNullable<UpdateLogRequest['edpAiConfiguration']>['aiPrompt']
              >
            >
              label="AI Prompt"
              name={EdpAiConfigFieldNames.AI_PROMPT}
              placeholder="Add AI prompt here"
              multiline
              rows={18}
            />
          </CompactFormGroup>
          <Button
            color="primary"
            variant="outlined"
            onClick={handleUploadTestFile}
            style={{ marginTop: '3px' }}
          >
            <ArrowLineUpIcon />
            Upload test file
          </Button>
          {projectId && (
            <StyledSelectProject
              label="Project"
              name="projectId"
              value={projectId}
              onChange={(e) => setProjectId(e.target.value as string)}
              options={Object.values(projectMap).map((project) => ({
                label: project.name,
                value: project.id,
              }))}
            />
          )}
          {!pristine && (
            <div ref={warningRef}>
              <StyledWarningInfoCard variant={InfoCardVariant.WARNING}>
                This Log has unsaved changes. For the most accurate test
                results, save changes before uploading a test file.
              </StyledWarningInfoCard>
            </div>
          )}

          {projectId && (
            <ErrorBoundary onError={closeUploadModal}>
              <LogEntriesImageUploadModal
                logId={logId}
                isOpen={isUploadModalOpen}
                onClose={closeUploadModal}
                organizationId={organizationId}
                projectId={projectId}
                isReadOnly
              />
            </ErrorBoundary>
          )}
        </CompactFormSection>
      </Content>
    </>
  );
}

export function EditLogPageAiConfig() {
  useDisableSubnav();
  const { logId, mode = Mode.new, organizationId } = useParams();
  if (!logId || !organizationId) {
    throw new Error(
      "EditLogPageAiConfig was rendered with a url that doesn't have logId or organizationId",
    );
  }
  const { log } = useLog({
    logId,
    organizationId,
    config: { useErrorBoundary: true },
  });
  const onSubmit = useSubmitLogDetails({ currentWizardStep: WizardStep.AI });

  // This prevents form reinitialization and switch animation issues
  if (!log) {
    return null;
  }

  return (
    <Page>
      <Form initialValues={log} onSubmit={onSubmit} subscription={{}}>
        {() => (
          <AiConfigFields
            isEditMode={mode === Mode.edit}
            logId={logId}
            organizationId={organizationId}
          />
        )}
      </Form>
    </Page>
  );
}
